import { useEffect, useState, useCallback } from "react";
import { Loader2, Save, CheckCircle2, AlertTriangle, TrendingUp, Globe, FlaskConical, SearchCheck } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const TABS = ["Overview", "Defaults", "Analytics", "Integrations"];
const DEFAULT_FIELDS = [
  ["title", "Platform Title"], ["description", "Platform Meta Description"],
  ["keywords", "Keywords"], ["og_image", "Default OG Image URL"],
  ["title_template", "Title Template ({page} | …)"], ["meta_description_template", "Description Template"],
  ["ga4_id", "Platform GA4 ID"], ["gads_id", "Platform Google Ads ID"], ["gads_label", "Google Ads Label"],
  ["gsc_verification", "Search Console Verification Token"],
];
const RANGES = [["1", "Today"], ["7", "7 Days"], ["30", "30 Days"], ["90", "90 Days"], ["365", "This Year"]];

export default function RootSeo() {
  const [tab, setTab] = useState("Overview");
  const [overview, setOverview] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [flags, setFlags] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [days, setDays] = useState("30");
  const [busy, setBusy] = useState("");

  const loadOverview = useCallback(() => api.get("/root/seo/overview").then((r) => setOverview(r.data)).catch((e) => toast.error(errMsg(e))), []);
  const loadDefaults = useCallback(() => api.get("/root/seo/defaults").then((r) => { setDefaults(r.data.defaults); setFlags({ pixel: r.data.meta_pixel_configured, capi: r.data.meta_capi_configured, test: r.data.meta_test_code_configured }); }).catch((e) => toast.error(errMsg(e))), []);
  const loadAnalytics = useCallback((d) => api.get(`/root/analytics/summary?days=${d}`).then((r) => setAnalytics(r.data)).catch((e) => toast.error(errMsg(e))), []);

  useEffect(() => { loadOverview(); loadDefaults(); loadAnalytics(days); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === "Analytics") loadAnalytics(days); }, [days, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveDefaults = async () => {
    setBusy("defaults");
    try { await api.put("/root/seo/defaults", defaults); toast.success("Global SEO defaults saved"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const testEvent = async () => {
    setBusy("test");
    try {
      const r = await api.post("/root/seo/test-event");
      toast.success(`Test event sent — Meta CAPI HTTP ${r.data.capi_http_status}. Verify in Events Manager → Test Events.`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const Card = ({ children, testid, className = "" }) => (
    <div data-testid={testid} className={`bg-card border border-border rounded-2xl p-6 ${className}`}>{children}</div>
  );

  return (
    <div data-testid="root-seo-page" className="max-w-6xl">
      <PageHeader title="SEO & Marketing" subtitle="Global defaults, indexing readiness, funnel analytics and tracking integrations." testid="root-seo-title" />
      <div className="flex flex-wrap gap-2 mb-6" data-testid="seo-tabs">
        {TABS.map((t) => (
          <button key={t} data-testid={`tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-full text-xs font-extrabold ${tab === t ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>{t}</button>
        ))}
      </div>

      {tab === "Overview" && (
        <Card testid="seo-overview">
          {!overview ? <Loader2 className="h-6 w-6 animate-spin" /> : (
            <>
              <div className="grid sm:grid-cols-3 gap-3 mb-5">
                {[["Total Websites", overview.total], ["SEO Ready", overview.ready], ["Action Required", overview.action_required]].map(([k, v]) => (
                  <div key={k} className="border border-border rounded-xl p-4 text-center">
                    <p className="font-display font-extrabold text-2xl" data-testid={`ov-${k.toLowerCase().replace(/ /g, "-")}`}>{v}</p>
                    <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mt-1">{k}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                {overview.websites.map((w) => (
                  <div key={w.website_id} data-testid={`seo-row-${w.slug}`} className="flex items-center gap-3 border border-border rounded-xl px-4 py-3">
                    <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold truncate">{w.name} <span className="text-muted-foreground font-mono text-xs">/{w.slug}</span></p>
                      {w.domain && <p className="text-xs text-muted-foreground font-mono truncate">{w.domain}</p>}
                    </div>
                    <span className={`text-[10px] font-extrabold px-2.5 py-1 rounded-full shrink-0 ${w.seo_status === "SEO READY" ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600"}`}
                      data-testid={`seo-status-${w.slug}`}>{w.seo_status}</span>
                    {w.issues.length > 0 && <span className="text-xs text-muted-foreground truncate max-w-[260px] hidden md:block">{w.issues.join(" · ")}</span>}
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      )}

      {tab === "Defaults" && defaults && (
        <Card testid="seo-defaults">
          <div className="grid sm:grid-cols-2 gap-3">
            {DEFAULT_FIELDS.map(([k, label]) => (
              <div key={k} className={k.includes("description") ? "sm:col-span-2" : ""}>
                <label className="text-xs font-extrabold uppercase tracking-widest text-muted-foreground">{label}</label>
                <Input data-testid={`default-${k}`} value={defaults[k] || ""} onChange={(e) => setDefaults({ ...defaults, [k]: e.target.value })} className="mt-1 h-10" />
              </div>
            ))}
          </div>
          <Button data-testid="save-defaults" onClick={saveDefaults} disabled={busy === "defaults"} className="rounded-full font-bold h-11 px-8 mt-5">
            {busy === "defaults" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save Defaults
          </Button>
          <p className="text-xs text-muted-foreground mt-3">Tenant-specific values always override these defaults.</p>
        </Card>
      )}

      {tab === "Analytics" && (
        <Card testid="seo-analytics">
          <div className="flex gap-2 mb-5" data-testid="range-toggles">
            {RANGES.map(([d, l]) => (
              <button key={d} data-testid={`range-${d}`} onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-full text-xs font-extrabold ${days === d ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{l}</button>
            ))}
          </div>
          {!analytics ? <Loader2 className="h-6 w-6 animate-spin" /> : (
            <>
              <div className="grid sm:grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                {[["Visitors (Page Views)", analytics.funnel.page_view], ["Add to Cart", analytics.funnel.add_to_cart],
                  ["Checkout Starts", analytics.funnel.begin_checkout], ["Purchases", analytics.funnel.purchase]].map(([k, v]) => (
                  <div key={k} className="border border-border rounded-xl p-4 text-center">
                    <p className="font-display font-extrabold text-2xl" data-testid={`funnel-${k.toLowerCase().replace(/[^a-z]/g, "-")}`}>{v}</p>
                    <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mt-1">{k}</p>
                  </div>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mb-6">
                <div className="border border-border rounded-xl p-4 text-center">
                  <p className="font-display font-extrabold text-2xl" data-testid="revenue-tracked">₹{Number(analytics.revenue_tracked).toLocaleString("en-IN")}</p>
                  <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mt-1">Revenue (tracked)</p>
                </div>
                <div className="border border-border rounded-xl p-4 text-center">
                  <p className="font-display font-extrabold text-2xl" data-testid="conversion-rate">{analytics.conversion_rate}%</p>
                  <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mt-1">Conversion Rate</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <p className="text-xs uppercase tracking-widest font-extrabold text-muted-foreground mb-2">Top Campaigns (UTM)</p>
                  {analytics.top_campaigns.length === 0 && <p className="text-sm text-muted-foreground">No campaign traffic yet.</p>}
                  {analytics.top_campaigns.map((c) => (
                    <div key={c.campaign} className="flex justify-between text-sm border-b border-border py-2" data-testid={`campaign-${c.campaign}`}>
                      <span className="font-bold truncate">{c.campaign}</span><span className="text-muted-foreground shrink-0">{c.events} events · ₹{c.value}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest font-extrabold text-muted-foreground mb-2">Top Landing Pages</p>
                  {analytics.top_pages.length === 0 && <p className="text-sm text-muted-foreground">No page views yet.</p>}
                  {analytics.top_pages.map((p) => (
                    <div key={p.page} className="flex justify-between text-sm border-b border-border py-2">
                      <span className="font-mono text-xs truncate">{p.page}</span><span className="text-muted-foreground shrink-0">{p.views}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </Card>
      )}

      {tab === "Integrations" && (
        <div className="space-y-5">
          <Card testid="int-meta">
            <p className="font-display font-bold text-base mb-3 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" /> Meta Pixel + Conversions API</p>
            <div className="grid sm:grid-cols-3 gap-3 mb-4">
              {[["Browser Pixel", flags.pixel], ["Conversions API (server)", flags.capi], ["Test Event Code", flags.test]].map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 border border-border rounded-xl px-3 py-2.5">
                  {v ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />}
                  <span className="text-xs font-bold">{k}: {v ? "Configured" : "Not configured"}</span>
                </div>
              ))}
            </div>
            <Button data-testid="meta-test-event" variant="outline" onClick={testEvent} disabled={busy === "test"} className="rounded-full font-bold">
              {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FlaskConical className="h-4 w-4 mr-2" />} Send Meta Test Event
            </Button>
            <p className="text-xs text-muted-foreground mt-3">Access token lives in the server-side secret store only — it is never sent to browsers, logs or API responses. Browser + server events share event_id for deduplication. Platform pixel fires on the main platform; tenants configure their own pixel in Store → SEO.</p>
          </Card>
          <Card testid="int-google">
            <p className="font-display font-bold text-base mb-3 flex items-center gap-2"><SearchCheck className="h-4 w-4 text-primary" /> Google Search Console / GA4 / Ads</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Verify miltroo.com in Search Console via the verification token in Defaults (meta tag) or DNS TXT at your DNS provider.
              Then submit <span className="font-mono text-xs">https://miltroo.com/sitemap.xml</span>. Custom-domain stores submit their own
              sitemap (<span className="font-mono text-xs">https://their-domain/sitemap.xml</span>). Sitemap submission helps discovery but does not guarantee indexing.
              GA4/Google Ads IDs go in Defaults (platform) or Store → SEO (tenant). OAuth-based Search Console API integration: add Google Cloud OAuth credentials to the server environment to enable.
            </p>
          </Card>
        </div>
      )}
    </div>
  );
}
